import { Fragment, useEffect, useState } from "react";
import { LogOut, PawPrint, Lock, Trash2, ImagePlus } from "lucide-react";
import { api, apiError, money } from "@/lib/helpers";
import { Reveal } from "@/components/Reveal";
import { toast } from "sonner";

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      toast.success(`Welcome back, ${data.name}.`);
      onLogin(data);
    } catch (err) {
      toast.error(apiError(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-pine-deep grain flex items-center justify-center p-4">
      <Reveal>
        <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-md rounded-3xl bg-sand p-8 sm:p-10 shadow-2xl">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Wag n Tails logo" className="h-14 w-14 rounded-full object-cover ring-2 ring-gold/70" />
            <div>
              <p className="font-serif text-xl text-charcoal">Staff Portal</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-bark">Wag n Tails Admin</p>
            </div>
          </div>
          <div className="mt-8 space-y-4">
            <input data-testid="admin-login-email" type="email" required placeholder="Admin email" value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-xl border border-stone-300 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-gold" />
            <input data-testid="admin-login-password" type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-xl border border-stone-300 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-gold" />
          </div>
          <button data-testid="admin-login-submit" type="submit" disabled={busy}
            className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-pine py-3.5 font-bold text-sand hover:bg-pine-mid transition-all disabled:opacity-60">
            <Lock className="h-4 w-4" /> {busy ? "Checking…" : "Sign in"}
          </button>
          <a href="/" data-testid="admin-back-home" className="mt-5 block text-center font-mono text-xs uppercase tracking-widest text-bark/60 hover:text-pine">
            ← Back to the sanctuary
          </a>
        </form>
      </Reveal>
    </div>
  );
}

function DetailRow({ label, value }) {
  if (!value) return null;
  return (
    <p className="text-sm"><span className="font-semibold text-charcoal">{label}:</span> <span className="text-bark">{value}</span></p>
  );
}

export default function Admin() {
  const [user, setUser] = useState("checking");
  const [tab, setTab] = useState("adoption");
  const [rows, setRows] = useState([]);
  const [donations, setDonations] = useState([]);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [tributes, setTributes] = useState([]);
  const [trForm, setTrForm] = useState({ name: "", years: "", story: "" });
  const [trFile, setTrFile] = useState(null);
  const [trBusy, setTrBusy] = useState(false);

  useEffect(() => {
    api.get("/auth/me").then(({ data }) => setUser(data)).catch(() => setUser(false));
  }, []);

  const loadAll = () => {
    api.get("/admin/submissions").then(({ data }) => setRows(data)).catch(() => {});
    api.get("/admin/donations").then(({ data }) => setDonations(data)).catch(() => {});
    api.get("/admin/orders").then(({ data }) => setOrders(data)).catch(() => {});
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/tributes").then(({ data }) => setTributes(data)).catch(() => {});
  };

  useEffect(() => { if (user && user !== "checking") loadAll(); }, [user]);

  const setStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/admin/submissions/${id}`, { status });
      setRows((r) => r.map((x) => (x.id === id ? data : x)));
      toast.success(`Marked ${status.toLowerCase()}.`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const setOrderStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/admin/orders/${id}`, { status });
      setOrders((o) => o.map((x) => (x.id === id ? data : x)));
      toast.success(`Order marked ${status.toLowerCase()}.`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const addTribute = async () => {
    if (!trForm.name.trim() || !trFile) {
      toast.error("A name and a photo are needed for a tribute.");
      return;
    }
    setTrBusy(true);
    try {
      const fd = new FormData();
      fd.append("name", trForm.name);
      fd.append("years", trForm.years);
      fd.append("story", trForm.story);
      fd.append("file", trFile);
      await api.post("/admin/tributes", fd);
      toast.success("Tribute added to the memorial page.");
      setTrForm({ name: "", years: "", story: "" });
      setTrFile(null);
      const { data } = await api.get("/tributes");
      setTributes(data);
    } catch (e) { toast.error(apiError(e)); } finally { setTrBusy(false); }
  };

  const deleteTribute = async (id) => {
    try {
      await api.delete(`/admin/tributes/${id}`);
      setTributes((t) => t.filter((x) => x.id !== id));
      toast.success("Tribute removed.");
    } catch (e) { toast.error(apiError(e)); }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(false);
  };

  if (user === "checking") {
    return <div className="min-h-screen bg-pine-deep flex items-center justify-center"><PawPrint className="h-8 w-8 text-gold-soft animate-pulse-soft" /></div>;
  }
  if (!user) return <Login onLogin={setUser} />;

  const shown = rows.filter((r) => r.kind === tab);

  return (
    <div className="min-h-screen bg-tinted">
      <header className="bg-pine-deep text-sand">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="logo" className="h-10 w-10 rounded-full object-cover ring-2 ring-gold/60" />
            <div>
              <p className="font-serif text-lg leading-tight">Admin Dashboard</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-sand/50">{user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" data-testid="admin-view-site" className="hidden sm:block font-mono text-xs uppercase tracking-widest text-sand/60 hover:text-gold-soft">View site</a>
            <button data-testid="admin-logout" onClick={logout} className="flex items-center gap-2 rounded-full border border-sand/25 px-4 py-2 text-sm hover:bg-sand/10 transition-colors">
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4" data-testid="admin-stats">
          {[["Adoptions", stats?.adoption], ["Volunteers", stats?.volunteer], ["Sponsors", stats?.sponsorship], ["Pledges", stats?.donations], ["Shop orders", orders.length]].map(([label, v], i) => (
            <div key={label} className="rounded-2xl bg-white border border-stone-200 p-5">
              <p className="font-serif text-3xl text-pine" data-testid={`admin-stat-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{v ?? "–"}</p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-bark">{label}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap gap-2" data-testid="admin-tabs">
          {[["adoption", "Adoption Applications"], ["volunteer", "Volunteers"], ["sponsorship", "Sponsorships"], ["donations", "Donation Pledges"], ["orders", "Merch Orders"], ["tributes", "Rescue Tributes"]].map(([key, label]) => (
            <button key={key} data-testid={`admin-tab-${key === "donations" || key === "orders" ? key : key}`} onClick={() => setTab(key)}
              className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-all ${tab === key ? "bg-pine text-sand" : "bg-white border border-stone-300 text-bark hover:border-pine"}`}>
              {label}
            </button>
          ))}
        </div>

        {tab === "tributes" && (
          <div data-testid="admin-tributes-panel">
            <div className="mt-6 rounded-3xl bg-white border border-stone-200 p-6 sm:p-8 shadow-sm">
              <h3 className="flex items-center gap-2 font-serif text-2xl text-charcoal"><ImagePlus className="h-5 w-5 text-gold-deep" /> Add a tribute</h3>
              <p className="mt-1 text-sm text-bark">Upload a photo, add their name, years (optional) and a few words from the heart.</p>
              <div className="mt-6 grid gap-4 sm:grid-cols-3">
                <input data-testid="tribute-name-input" placeholder="Their name *" value={trForm.name} onChange={(e) => setTrForm({ ...trForm, name: e.target.value })}
                  className="rounded-xl border border-stone-300 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-gold" />
                <input data-testid="tribute-years-input" placeholder="Years (e.g. 2014 – 2025)" value={trForm.years} onChange={(e) => setTrForm({ ...trForm, years: e.target.value })}
                  className="rounded-xl border border-stone-300 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-gold" />
                <label className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed border-stone-300 px-4 py-3 text-sm text-bark hover:border-gold hover:text-gold-deep transition-colors">
                  <ImagePlus className="h-4 w-4" /> {trFile ? trFile.name.slice(0, 24) : "Choose a photo *"}
                  <input data-testid="tribute-photo-input" type="file" accept="image/*" className="hidden"
                    onChange={(e) => setTrFile(e.target.files?.[0] || null)} />
                </label>
                <textarea data-testid="tribute-story-input" rows={3} placeholder="A few words from the heart…" value={trForm.story} onChange={(e) => setTrForm({ ...trForm, story: e.target.value })}
                  className="rounded-xl border border-stone-300 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-gold sm:col-span-3" />
              </div>
              <button data-testid="tribute-add-btn" onClick={addTribute} disabled={trBusy}
                className="mt-5 rounded-full bg-pine px-8 py-3.5 font-bold text-sand hover:bg-pine-mid transition-all disabled:opacity-60">
                {trBusy ? "Uploading…" : "Add to the tribute wall"}
              </button>
            </div>

            <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="admin-tributes-list">
              {tributes.map((t) => (
                <div key={t.id} data-testid={`admin-tribute-${t.id}`} className="overflow-hidden rounded-2xl bg-white border border-stone-200">
                  <img src={t.image} alt={t.name} className="aspect-[4/3] w-full object-cover" />
                  <div className="p-4">
                    <p className="font-serif text-lg text-charcoal">{t.name}</p>
                    {t.years && <p className="font-mono text-xs text-gold-deep">{t.years}</p>}
                    <button data-testid={`tribute-delete-${t.id}`} onClick={() => deleteTribute(t.id)}
                      className="mt-3 flex items-center gap-1.5 rounded-full border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50 transition-colors">
                      <Trash2 className="h-3.5 w-3.5" /> Remove
                    </button>
                  </div>
                </div>
              ))}
              {!tributes.length && <p className="col-span-full py-8 text-center text-sm text-bark">No tributes yet — add the first above.</p>}
            </div>
          </div>
        )}

        <div className={tab === "tributes" ? "hidden" : "mt-6 overflow-x-auto rounded-3xl bg-white border border-stone-200 shadow-sm"}>
          {tab === "donations" && (
            <table className="w-full text-left text-sm" data-testid="admin-donations-table">
              <thead className="bg-tinted font-mono text-[10px] uppercase tracking-widest text-bark">
                <tr>{["Date", "Receipt", "Donor", "Email", "Amount", "Status"].map((h) => <th key={h} className="px-5 py-4">{h}</th>)}</tr>
              </thead>
              <tbody>
                {donations.map((d) => (
                  <tr key={d.id} className="border-t border-stone-100" data-testid={`admin-donation-row-${d.receipt_id}`}>
                    <td className="px-5 py-4 text-bark whitespace-nowrap">{new Date(d.created_at).toLocaleDateString()}</td>
                    <td className="px-5 py-4 font-mono text-xs">{d.receipt_id}</td>
                    <td className="px-5 py-4 font-semibold">{d.donor_name}</td>
                    <td className="px-5 py-4 text-bark">{d.email}</td>
                    <td className="px-5 py-4 font-mono font-bold text-pine">${d.amount}{d.monthly ? "/mo" : ""}</td>
                    <td className="px-5 py-4">
                      <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${d.status === "paid" ? "bg-pine text-sand" : "bg-gold/20 text-gold-deep"}`}>{d.status}</span>
                    </td>
                  </tr>
                ))}
                {!donations.length && <tr><td colSpan="6" className="px-5 py-10 text-center text-bark">No pledges yet.</td></tr>}
              </tbody>
            </table>
          )}

          {tab === "orders" && (
            <table className="w-full text-left text-sm" data-testid="admin-orders-table">
              <thead className="bg-tinted font-mono text-[10px] uppercase tracking-widest text-bark">
                <tr>{["Date", "Order", "Customer", "Items", "Total", "Status"].map((h) => <th key={h} className="px-5 py-4">{h}</th>)}</tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id} className="border-t border-stone-100 align-top" data-testid={`admin-order-row-${o.order_id}`}>
                    <td className="px-5 py-4 text-bark whitespace-nowrap">{new Date(o.created_at).toLocaleDateString()}</td>
                    <td className="px-5 py-4 font-mono text-xs">{o.order_id}</td>
                    <td className="px-5 py-4">
                      <span className="font-semibold">{o.customer_name}</span>
                      <span className="block text-xs text-bark">{o.email}</span>
                      {o.note && <span className="block text-xs text-bark italic">“{o.note}”</span>}
                    </td>
                    <td className="px-5 py-4 text-bark">
                      {o.items.map((it, i) => <span key={i} className="block text-xs">{it.qty}× {it.name}</span>)}
                    </td>
                    <td className="px-5 py-4 font-mono font-bold text-pine">{money(o.total)}</td>
                    <td className="px-5 py-4">
                      <select data-testid={`admin-order-status-${o.order_id}`} value={o.status} onChange={(e) => setOrderStatus(o.id, e.target.value)}
                        className="rounded-full border border-stone-300 bg-white px-3 py-1.5 text-xs font-semibold outline-none focus:ring-2 focus:ring-gold">
                        {["Pending", "Packed", "Shipped"].map((s) => <option key={s}>{s}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
                {!orders.length && <tr><td colSpan="6" className="px-5 py-10 text-center text-bark">No shop orders yet.</td></tr>}
              </tbody>
            </table>
          )}

          {tab !== "donations" && tab !== "orders" && (
            <table className="w-full text-left text-sm" data-testid="admin-submissions-table">
              <thead className="bg-tinted font-mono text-[10px] uppercase tracking-widest text-bark">
                <tr>{["Date", "Name", "Contact", "Details", "Status"].map((h) => <th key={h} className="px-5 py-4">{h}</th>)}</tr>
              </thead>
              <tbody>
                {shown.map((r) => (
                  <Fragment key={r.id}>
                    <tr className="border-t border-stone-100 hover:bg-tinted/50" data-testid={`admin-row-${r.id}`}>
                      <td className="px-5 py-4 text-bark whitespace-nowrap">{new Date(r.created_at).toLocaleDateString()}</td>
                      <td className="px-5 py-4 font-semibold">{r.name}</td>
                      <td className="px-5 py-4 text-bark">{r.email}{r.phone && <span className="block text-xs">{r.phone}</span>}</td>
                      <td className="px-5 py-4">
                        <button data-testid={`admin-expand-${r.id}`} onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                          className="font-mono text-xs underline text-pine hover:text-gold-deep">
                          {expanded === r.id ? "Hide" : "View"}
                        </button>
                        {expanded === r.id && (
                          <div className="mt-2 min-w-[240px] space-y-1 rounded-xl bg-tinted p-3" data-testid={`admin-details-${r.id}`}>
                            <DetailRow label="Interest" value={r.data?.animal_name} />
                            <DetailRow label="City" value={r.data?.city} />
                            <DetailRow label="Housing" value={r.data?.housing} />
                            <DetailRow label="Experience" value={r.data?.experience || r.data?.role} />
                            <DetailRow label="Availability" value={r.data?.availability} />
                            <DetailRow label="Household" value={r.data?.household} />
                            <DetailRow label="Amount" value={r.data?.amount ? `$${r.data.amount}/mo` : ""} />
                            <DetailRow label="Message" value={r.data?.message} />
                          </div>
                        )}
                      </td>
                      <td className="px-5 py-4">
                        <select data-testid={`admin-status-select-${r.id}`} value={r.status} onChange={(e) => setStatus(r.id, e.target.value)}
                          className="rounded-full border border-stone-300 bg-white px-3 py-1.5 text-xs font-semibold outline-none focus:ring-2 focus:ring-gold">
                          {["Pending", "Approved", "Reviewed"].map((s) => <option key={s}>{s}</option>)}
                        </select>
                      </td>
                    </tr>
                  </Fragment>
                ))}
                {!shown.length && <tr><td colSpan="5" className="px-5 py-10 text-center text-bark">No submissions yet.</td></tr>}
              </tbody>
            </table>
          )}
        </div>
      </main>
    </div>
  );
}
